import { Fragment } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import InputNumber from '@/design_system/InputNumber';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { Label } from '@/design_system/Label/Label';
import Stack from '@/design_system/Stack';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';
import { useSparePartTypesOrganization } from '@/models/sparePartType';
import { useDatabaseTranslation } from '@/services/i18n';

export type SparePartTypesInputsValue = {
  id: string | null;
  defaultQuantity: number | null;
  key: string;
}[];

export const SparePartTypesInputs = ({
  sparePartTypes,
  setSparePartTypes,
  showErrors,
}: {
  sparePartTypes: SparePartTypesInputsValue;
  setSparePartTypes: (sparePartTypes: SparePartTypesInputsValue) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  const { data: allSparePartTypes = [] } = useSparePartTypesOrganization();

  return (
    <Stack gap="0.5rem">
      <Label label={t({ id: 'settings.actions.spare-part-inputs', message: 'Spare parts' })} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          columnGap: '0.25rem',
          rowGap: '0.5rem',
        }}
      >
        {sparePartTypes.map((sparePartType) => {
          const selectedSparePartType = allSparePartTypes.find(({ id }) => id === sparePartType.id);

          return (
            <Fragment key={sparePartType.key}>
              <InputSelect
                // style={{ flex: 1 }}
                options={allSparePartTypes.filter(
                  ({ id }) => sparePartType.id === id || !sparePartTypes.some((sp) => sp.id === id)
                )}
                value={selectedSparePartType}
                aria-label={t({
                  id: 'settings.actions.spare-part-inputs.label',
                  message: 'Spare part',
                })}
                placeholder={t({
                  id: 'settings.actions.spare-part-inputs.label',
                  message: 'Spare part',
                })}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => _db(option.name)}
                onChange={(option) =>
                  setSparePartTypes(
                    sparePartTypes.map((sp) =>
                      sparePartType.key === sp.key
                        ? {
                            ...sparePartType,
                            id: option?.id ?? null,
                          }
                        : sp
                    )
                  )
                }
                isInvalid={showErrors && !sparePartType.id}
              />
              <InputNumber
                aria-label={t({
                  id: 'settings.actions.spare-part-inputs.quantity',
                  message: 'Default quantity',
                })}
                placeholder={t({
                  id: 'settings.actions.spare-part-inputs.quantity',
                  message: 'Default quantity',
                })}
                value={sparePartType.defaultQuantity ?? NaN}
                onChange={(value) =>
                  setSparePartTypes(
                    sparePartTypes.map((sp) =>
                      sparePartType.key === sp.key
                        ? {
                            ...sparePartType,
                            defaultQuantity: Number.isNaN(value)
                              ? null
                              : Math.round(Math.abs(value)),
                          }
                        : sp
                    )
                  )
                }
              />
              <Button
                ariaLabel={t({
                  id: 'settings.actions.spare-part-inputs.delete',
                  message: `Remove`,
                })}
                tooltip={t({
                  id: 'settings.actions.spare-part-inputs.delete',
                  message: `Remove`,
                })}
                iconOnly
                size="large"
                variant="secondary-danger"
                onPress={() =>
                  setSparePartTypes(sparePartTypes.filter((sp) => sp.key !== sparePartType.key))
                }
              >
                <IconTrash />
              </Button>
            </Fragment>
          );
        })}
      </div>
      {sparePartTypes.length < allSparePartTypes.length && (
        <div>
          <Button
            variant="link-subtle"
            size="small"
            onPress={() => {
              setSparePartTypes([
                ...sparePartTypes,
                {
                  key: crypto.randomUUID(),
                  id: null,
                  defaultQuantity: null,
                },
              ]);
            }}
            disabled={!!sparePartTypes.length && !sparePartTypes.at(-1)?.id}
          >
            <IconAdd />
            <Trans id="settings.actions.spare-part-inputs.add">Add a spare part</Trans>
          </Button>
        </div>
      )}
    </Stack>
  );
};
